import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  CheckCircle, ArrowRight, ArrowLeft, Loader2, Building2, 
  Briefcase, Clock, Calendar, Link2, Copy, Check, Plus, Trash2,
  ExternalLink, MapPin, CreditCard, Smartphone, CalendarCheck, MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SQUARE_ACTIVATION_LINK = 'https://square.link/u/J2GrZj2T';

const PROVIDER_CATEGORIES = [
  { value: 'FARRIER', label: 'Farrier', description: 'Hoof care and shoeing' },
  { value: 'RIDING_INSTRUCTOR', label: 'Riding Instructor', description: 'Lessons and training' },
  { value: 'TRAINER', label: 'Horse Trainer', description: 'Horse training services' },
  { value: 'HAULING', label: 'Horse Hauling', description: 'Transport services' },
  { value: 'RANCH_SERVICE', label: 'Ranch Service', description: 'General ranch work' },
  { value: 'FENCE_REPAIR', label: 'Fence Repair', description: 'Fencing installation and repair' },
  { value: 'EQUIPMENT_WORK', label: 'Equipment Work', description: 'Machinery and equipment' },
  { value: 'VET', label: 'Mobile Vet', description: 'Veterinary services' },
  { value: 'GUIDED_TRAIL_RIDES', label: 'Guided Trail Rides', description: 'Guided trail riding experiences' },
  { value: 'RIDING_STABLE', label: 'Riding Stable', description: 'Riding stable and arena services' },
  { value: 'STABLE_SERVICES', label: 'Stable Services', description: 'Boarding, stall care, and stable upkeep' },
  { value: 'BARN_MANAGEMENT', label: 'Other', description: 'Other services' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [provider, setProvider] = useState(null);
  const [copied, setCopied] = useState(false);

  // Step 1 - Business Info
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [serviceRadius, setServiceRadius] = useState('25');

  // Step 2 - Categories (multi-select, RW-FIX-003)
  const [categories, setCategories] = useState([]);
  const toggleCategory = (value) => {
    setCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  // Step 3 - Services
  const [services, setServices] = useState([
    { _key: 'svc-0', name: '', pricingModel: 'PER_SERVICE', duration: '', price: '', depositEnabled: false, depositType: 'FIXED', depositAmount: '' }
  ]);

  // Step 4 - Availability
  const [availability, setAvailability] = useState([
    { _key: 'avail-0', dayOfWeek: 1, startTime: '08:00', endTime: '17:00' }
  ]);

  // Step 5 - Promo Code
  const [promoCode, setPromoCode] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoApplied, setPromoApplied] = useState(false);

  useEffect(() => {
    loadProviderData();
  }, []);

  const loadProviderData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/dashboard/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      setProvider(data);
      
      // Pre-fill form with existing data
      if (data.businessName) setBusinessName(data.businessName);
      if (data.ownerName) setOwnerName(data.ownerName);
      if (data.phone) setPhone(data.phone);
      if (data.city) setCity(data.city);
      if (data.state) setState(data.state);
      if (data.serviceRadiusMiles) setServiceRadius(data.serviceRadiusMiles.toString());
      if (data.providerCategories && data.providerCategories.length > 0) {
        setCategories(data.providerCategories);
      } else if (data.providerCategory) {
        setCategories([data.providerCategory]);
      }
      
      // Check if onboarding is already completed
      if (data.onboardingCompleted) {
        navigate('/dashboard');
        return;
      }
      
      // Load existing services
      const servicesRes = await axios.get(`${API_URL}/api/dashboard/services`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (servicesRes.data.length > 0) {
        setServices(servicesRes.data.map(s => ({
          id: s.id,
          name: s.name,
          pricingModel: s.pricingModel || 'PER_SERVICE',
          duration: s.durationMinutes ? s.durationMinutes.toString() : '',
          price: s.basePriceCents ? (s.basePriceCents / 100).toString() : '',
          depositEnabled: s.depositEnabled || false,
          depositType: s.depositType || 'FIXED',
          depositAmount: s.depositFixedAmountCents ? (s.depositFixedAmountCents / 100).toString() : ''
        })));
      }
      
      // Load existing availability
      const availRes = await axios.get(`${API_URL}/api/dashboard/availability`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (availRes.data.length > 0) {
        setAvailability(availRes.data.map(a => ({
          id: a.id,
          dayOfWeek: a.dayOfWeek,
          startTime: a.startTime,
          endTime: a.endTime
        })));
      }
    } catch (error) {
      console.error('Error loading provider data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveStep1 = async () => {
    if (!businessName || !ownerName || !phone || !city || !state) {
      toast.error('Please fill in all required fields');
      return false;
    }
    
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/api/dashboard/profile`, {
        businessName,
        ownerName,
        phone,
        city,
        state,
        serviceRadiusMiles: parseInt(serviceRadius)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      toast.error('Failed to save business info');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveStep2 = async () => {
    if (!categories || categories.length === 0) {
      toast.error('Please select at least one provider type');
      return false;
    }
    
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/api/dashboard/profile`, {
        providerCategories: categories
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (error) {
      toast.error('Failed to save provider types');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveStep3 = async () => {
    const validServices = services.filter(s => s.name.trim());
    if (validServices.length === 0) {
      toast.error('Please add at least one service');
      return false;
    }
    
    try {
      setSaving(true);
      
      for (const service of validServices) {
        const serviceData = {
          name: service.name,
          pricingModel: service.pricingModel || 'PER_SERVICE',
          durationMinutes: service.duration && service.duration !== 'none' ? parseInt(service.duration) : null,
          basePriceCents: service.price ? Math.round(parseFloat(service.price) * 100) : null,
          depositEnabled: service.depositEnabled,
          depositType: service.depositType,
          depositFixedAmountCents: service.depositEnabled && service.depositType === 'FIXED' && service.depositAmount 
            ? Math.round(parseFloat(service.depositAmount) * 100) 
            : null
        };
        
        if (service.id) {
          await axios.patch(`${API_URL}/api/dashboard/services/${service.id}`, serviceData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post(`${API_URL}/api/dashboard/services`, serviceData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      return true;
    } catch (error) {
      toast.error('Failed to save services');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveStep4 = async () => {
    const validAvailability = availability.filter(a => a.startTime && a.endTime);
    if (validAvailability.length === 0) {
      toast.error('Please add at least one availability slot');
      return false;
    }
    
    try {
      setSaving(true);
      
      for (const slot of validAvailability) {
        const slotData = {
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime
        };
        
        if (slot.id) {
          await axios.patch(`${API_URL}/api/dashboard/availability/${slot.id}`, slotData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } else {
          await axios.post(`${API_URL}/api/dashboard/availability`, slotData, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }
      }
      return true;
    } catch (error) {
      toast.error('Failed to save availability');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const completeOnboarding = async () => {
    try {
      setSaving(true);
      await axios.patch(`${API_URL}/api/dashboard/profile`, {
        onboardingCompleted: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Reload provider data
      const response = await axios.get(`${API_URL}/api/dashboard/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProvider(response.data);
      
      return true;
    } catch (error) {
      toast.error('Failed to complete onboarding');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    let success = false;
    
    switch (currentStep) {
      case 1:
        success = await saveStep1();
        break;
      case 2:
        success = await saveStep2();
        break;
      case 3:
        success = await saveStep3();
        break;
      case 4:
        success = await saveStep4();
        break;
      case 5:
        success = await completeOnboarding();
        if (success) {
          setCurrentStep(6); // Go to completion screen
        }
        return;
      default:
        success = true;
    }
    
    if (success) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => Math.max(1, prev - 1));
  };

  const addService = () => {
    setServices([...services, { _key: `svc-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, name: '', pricingModel: 'PER_SERVICE', duration: '', price: '', depositEnabled: false, depositType: 'FIXED', depositAmount: '' }]);
  };

  const removeService = (index) => {
    if (services.length > 1) {
      setServices(services.filter((_, i) => i !== index));
    }
  };

  const updateService = (index, field, value) => {
    const updated = [...services];
    updated[index][field] = value;
    setServices(updated);
  };

  const addAvailability = () => {
    setAvailability([...availability, { _key: `avail-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, dayOfWeek: 1, startTime: '08:00', endTime: '17:00' }]);
  };

  const removeAvailability = (index) => {
    if (availability.length > 1) {
      setAvailability(availability.filter((_, i) => i !== index));
    }
  };

  const updateAvailability = (index, field, value) => {
    const updated = [...availability];
    updated[index][field] = value;
    setAvailability(updated);
  };

  const getBookingUrl = () => {
    const slug = provider?.slug || businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `${window.location.origin}/providers/${slug}`;
  };

  const copyBookingLink = () => {
    navigator.clipboard.writeText(getBookingUrl());
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoValidating(true);
    try {
      const resp = await axios.post(`${API_URL}/api/promo-codes/apply`, {
        code: promoCode.trim(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (resp.data.success) {
        setPromoApplied(true);
        toast.success(resp.data.message || 'Promo code applied! Account activated.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid promo code');
    } finally {
      setPromoValidating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Completion Screen
  if (currentStep === 6) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background">
        <nav className="bg-card border-b">
          <div className="max-w-2xl mx-auto px-4 py-4">
            <Logo variant="compact" size="sm" />
          </div>
        </nav>
        
        <div className="max-w-lg mx-auto px-4 py-12">
          <Card className="text-center">
            <CardHeader>
              <div className="flex justify-center mb-4">
                <div className="bg-green-100 rounded-full p-6">
                  <CheckCircle className="h-16 w-16 text-green-600" />
                </div>
              </div>
              <CardTitle className="text-2xl">Your booking page is live!</CardTitle>
              <CardDescription className="text-base mt-2">
                Congratulations! Clients can now book your services online.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 bg-muted rounded-lg">
                <p className="text-sm text-muted-foreground mb-2">Your booking link:</p>
                <p className="font-mono text-sm break-all">{getBookingUrl()}</p>
              </div>
              
              <Button 
                onClick={copyBookingLink}
                variant="outline"
                className="w-full"
              >
                {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                {copied ? 'Copied!' : 'Copy Booking Link'}
              </Button>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => window.open(getBookingUrl(), '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View Page
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => navigate('/dashboard')}
                >
                  Go To Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const steps = [
    { number: 1, title: 'Business Info', icon: Building2 },
    { number: 2, title: 'Category', icon: Briefcase },
    { number: 3, title: 'Services', icon: Clock },
    { number: 4, title: 'Availability', icon: Calendar },
    { number: 5, title: 'Activate', icon: CreditCard },
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="bg-card border-b sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <Logo variant="compact" size="sm" />
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
              Skip for now
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            {steps.map((step, index) => (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${
                    currentStep > step.number 
                      ? 'bg-primary text-primary-foreground'
                      : currentStep === step.number
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground'
                  }`}>
                    {currentStep > step.number ? (
                      <Check className="h-5 w-5" />
                    ) : (
                      step.number
                    )}
                  </div>
                  <span className={`text-xs mt-1 hidden sm:block ${
                    currentStep >= step.number ? 'text-foreground' : 'text-muted-foreground'
                  }`}>
                    {step.title}
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-1 mx-2 rounded ${
                    currentStep > step.number ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {React.createElement(steps[currentStep - 1].icon, { className: 'h-5 w-5' })}
              {currentStep === 5 ? 'Activate Your RanchWorks Account' : steps[currentStep - 1].title}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about your business'}
              {currentStep === 2 && 'What type of services do you provide?'}
              {currentStep === 3 && 'Add the services you offer'}
              {currentStep === 4 && 'Set your working hours'}
              {currentStep === 5 && 'Your booking page is ready. Activate your account to start receiving bookings and share your booking link with clients.'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1 - Business Info */}
            {currentStep === 1 && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="businessName">Business Name *</Label>
                    <Input
                      id="businessName"
                      value={businessName}
                      onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Happy Hooves Farrier"
                      data-testid="onboarding-business-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ownerName">Owner Name *</Label>
                    <Input
                      id="ownerName"
                      value={ownerName}
                      onChange={(e) => setOwnerName(e.target.value)}
                      placeholder="John Smith"
                      data-testid="onboarding-owner-name"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                    data-testid="onboarding-phone"
                  />
                </div>
                
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  <div className="space-y-2 col-span-1">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder="Austin"
                      data-testid="onboarding-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <Select value={state} onValueChange={setState}>
                      <SelectTrigger id="state" data-testid="onboarding-state">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="radius">Radius (mi)</Label>
                    <Select value={serviceRadius} onValueChange={setServiceRadius}>
                      <SelectTrigger id="radius">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[10, 25, 50, 75, 100].map((r) => (
                          <SelectItem key={r} value={r.toString()}>{r} miles</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}

            {/* Step 2 - Provider Types (multi-select, RW-FIX-003) */}
            {currentStep === 2 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground" data-testid="category-multiselect-hint">
                  Select all that apply — you can offer more than one type of service.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="category-grid">
                  {PROVIDER_CATEGORIES.map((cat) => {
                    const selected = categories.includes(cat.value);
                    return (
                      <div
                        key={cat.value}
                        className={`relative p-4 border rounded-lg cursor-pointer transition-all ${
                          selected
                            ? 'border-primary bg-primary/5 ring-2 ring-primary'
                            : 'hover:border-primary/50'
                        }`}
                        onClick={() => toggleCategory(cat.value)}
                        data-testid={`category-${cat.value}`}
                      >
                        {selected && (
                          <Check className="absolute top-3 right-3 h-4 w-4 text-primary" data-testid={`category-${cat.value}-check`} />
                        )}
                        <p className="font-semibold pr-6">{cat.label}</p>
                        <p className="text-sm text-muted-foreground">{cat.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Step 3 - Services */}
            {currentStep === 3 && (
              <div className="space-y-4">
                {services.map((service, index) => (
                  <div key={service.id || service._key || `svc-fallback-${index}`} className="p-4 border rounded-lg space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium">Service {index + 1}</span>
                      {services.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeService(index)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                    
                    {/* Service Name */}
                    <div className="space-y-2">
                      <Label>Service Name *</Label>
                      <Input
                        value={service.name}
                        onChange={(e) => updateService(index, 'name', e.target.value)}
                        placeholder="e.g., Full Trim, Training Session"
                        data-testid={`service-name-${index}`}
                      />
                    </div>

                    {/* Pricing Model */}
                    <div className="space-y-2">
                      <Label>Pricing Model</Label>
                      <Select 
                        value={service.pricingModel || 'PER_SERVICE'} 
                        onValueChange={(v) => updateService(index, 'pricingModel', v)}
                      >
                        <SelectTrigger data-testid={`pricing-model-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="PER_SERVICE">Per Service (flat rate)</SelectItem>
                          <SelectItem value="PER_HORSE">Per Horse</SelectItem>
                          <SelectItem value="PER_HOUR">Per Hour</SelectItem>
                          <SelectItem value="PER_MONTH">Per Month</SelectItem>
                          <SelectItem value="PER_VISIT">Per Visit</SelectItem>
                          <SelectItem value="CUSTOM_QUOTE">Custom Quote</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Price — hidden for CUSTOM_QUOTE */}
                      {service.pricingModel !== 'CUSTOM_QUOTE' && (
                        <div className="space-y-2">
                          <Label>
                            {service.pricingModel === 'PER_HORSE' ? 'Price per Horse ($)' :
                             service.pricingModel === 'PER_HOUR' ? 'Hourly Rate ($)' :
                             service.pricingModel === 'PER_MONTH' ? 'Monthly Rate ($)' :
                             service.pricingModel === 'PER_VISIT' ? 'Price per Visit ($)' :
                             'Price ($)'}
                          </Label>
                          <Input
                            type="number"
                            value={service.price}
                            onChange={(e) => updateService(index, 'price', e.target.value)}
                            placeholder="0.00"
                            data-testid={`service-price-${index}`}
                          />
                        </div>
                      )}

                      {/* Duration — optional, scheduling only */}
                      {service.pricingModel !== 'PER_MONTH' && (
                        <div className="space-y-2">
                          <Label>Duration <span className="text-xs text-muted-foreground">(optional, for scheduling)</span></Label>
                          <Select 
                            value={service.duration || ''} 
                            onValueChange={(v) => updateService(index, 'duration', v)}
                          >
                            <SelectTrigger data-testid={`service-duration-${index}`}>
                              <SelectValue placeholder="Not set" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Not set</SelectItem>
                              {[15, 30, 45, 60, 90, 120, 180, 240].map((d) => (
                                <SelectItem key={d} value={d.toString()}>{d} min</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>

                    {/* Deposit — only if price is set */}
                    {service.pricingModel !== 'CUSTOM_QUOTE' && (
                      <div className="space-y-2">
                        <Label>Deposit ($) <span className="text-xs text-muted-foreground">(optional)</span></Label>
                        <Input
                          type="number"
                          value={service.depositAmount}
                          onChange={(e) => {
                            updateService(index, 'depositAmount', e.target.value);
                            updateService(index, 'depositEnabled', !!e.target.value);
                          }}
                          placeholder="Optional"
                        />
                      </div>
                    )}
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={addService}
                  data-testid="add-service-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Another Service
                </Button>
              </div>
            )}

            {/* Step 4 - Availability */}
            {currentStep === 4 && (
              <div className="space-y-4">
                {availability.map((slot, index) => (
                  <div key={slot.id || slot._key || `avail-fallback-${index}`} className="flex items-center gap-3">
                    <Select 
                      value={slot.dayOfWeek.toString()} 
                      onValueChange={(v) => updateAvailability(index, 'dayOfWeek', parseInt(v))}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS_OF_WEEK.map((d) => (
                          <SelectItem key={d.value} value={d.value.toString()}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    
                    <Input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) => updateAvailability(index, 'startTime', e.target.value)}
                      className="w-28"
                    />
                    
                    <span className="text-muted-foreground">to</span>
                    
                    <Input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) => updateAvailability(index, 'endTime', e.target.value)}
                      className="w-28"
                    />
                    
                    {availability.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAvailability(index)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={addAvailability}
                  data-testid="add-availability-button"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add Another Day
                </Button>
                
                <p className="text-sm text-muted-foreground">
                  Tip: Add multiple entries for the same day if you have breaks.
                </p>
              </div>
            )}

            {/* Step 5 - Activate Account */}
            {currentStep === 5 && (
              <div className="space-y-6">
                {/* Benefits */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Link2 className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Your own booking page</p>
                      <p className="text-sm text-muted-foreground">A professional page clients can use to book</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <MessageSquare className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Clients book instead of texting</p>
                      <p className="text-sm text-muted-foreground">No more back-and-forth scheduling</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                    <div className="bg-primary/10 p-2 rounded-full">
                      <Smartphone className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">Manage appointments from your phone</p>
                      <p className="text-sm text-muted-foreground">Accept, reschedule, or cancel anytime</p>
                    </div>
                  </div>
                </div>

                {/* Booking URL Preview */}
                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-2">Your booking page:</p>
                  <p className="font-mono text-sm break-all" data-testid="booking-url">
                    {getBookingUrl()}
                  </p>
                  <Button 
                    onClick={copyBookingLink}
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                  >
                    {copied ? <Check className="mr-2 h-4 w-4" /> : <Copy className="mr-2 h-4 w-4" />}
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                </div>

                {/* Promo Code */}
                <div className="p-4 border rounded-lg space-y-3">
                  <Label className="text-sm font-medium">Have a promo code?</Label>
                  {promoApplied ? (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="text-green-800 font-medium">Promo code applied — account activated!</span>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter promo code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value)}
                        className="uppercase"
                        data-testid="promo-code-input"
                      />
                      <Button
                        onClick={handleApplyPromo}
                        disabled={promoValidating || !promoCode.trim()}
                        data-testid="apply-promo-btn"
                      >
                        {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Activation Button (hidden if promo applied) */}
                {!promoApplied && (
                  <div className="text-center pt-4">
                    <div className="relative flex items-center justify-center my-3">
                      <hr className="w-full border-muted" />
                      <span className="absolute bg-card px-3 text-xs text-muted-foreground uppercase">or</span>
                    </div>
                    <Button 
                      asChild
                      size="lg"
                      className="w-full h-14 text-lg font-semibold bg-primary hover:bg-primary/90"
                      data-testid="activate-button"
                    >
                      <a href={SQUARE_ACTIVATION_LINK} target="_blank" rel="noopener noreferrer">
                        <CreditCard className="mr-2 h-5 w-5" />
                        Add Payment to Continue RanchWorks
                      </a>
                    </Button>
                    <p className="text-sm text-muted-foreground mt-3">
                      Secure payment via Square. Cancel anytime.
                    </p>
                  </div>
                )}

                {/* Pending Activation Notice (hidden if promo applied) */}
                {!promoApplied && (
                  <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Your account setup is complete, but activation is still pending. 
                      If activation fails after payment, please contact support if this continues.
                    </p>
                  </div>
                )}

                {/* Success message if promo applied */}
                {promoApplied && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-800 font-medium">Your account is now active!</p>
                    <p className="text-sm text-green-700 mt-1">Share your booking link with clients to start receiving bookings.</p>
                    <Button
                      className="mt-4"
                      onClick={() => navigate('/dashboard')}
                      data-testid="go-to-dashboard-btn"
                    >
                      Go to Dashboard
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Navigation Buttons - Hide on Step 5 */}
        {currentStep < 5 && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1 || saving}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            
            <Button
              onClick={handleNext}
              disabled={saving}
              data-testid="onboarding-next-button"
            >
              {saving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <>
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        )}

        {/* Back to Dashboard on Step 5 */}
        {currentStep === 5 && (
          <div className="flex justify-center mt-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
